/** Named constants — replaces all magic numbers and strings from the original IIFE. */

import type { ClickIdConfig, DataAttributeConfig } from "./types.js";

// --- Timing ---
export const DEBOUNCE_DELAY_MS = 100;
export const POLLING_INTERVAL_MS = 500;
export const DEDUP_WINDOW_MS = 5000;
export const DEDUP_PRUNE_INTERVAL_MS = 30_000;
export const DEDUP_PRUNE_AGE_MS = 10_000;
export const DEDUP_MAX_ENTRIES = 50;
export const BUFFER_MAX_ITEMS = 50;
export const BUFFER_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

// --- Cookies ---
export const COOKIE_MAX_AGE_CLICK_ID_DAYS = 90;
export const COOKIE_MAX_AGE_EXTERNAL_ID_DAYS = 390;

// --- Session/Local storage keys ---
export const STORAGE_KEY_BUFFER = "_tst_buffer";
export const STORAGE_KEY_SELECTOR = "_tst_selector";
/**
 * A random value this browser keeps about itself, sent along with the config
 * request so the server can tell that two requests arriving at the same instant
 * come from the same browser. It identifies nothing on its own and is never
 * stored server-side beyond a minute; see `utils/anchor-mint-cache.ts`.
 */
export const STORAGE_KEY_BOOT = "_tst_boot";

// --- Failover ---
// Edge failover ingest endpoint (Cloudflare Worker). Baked in at build time via
// esbuild `define`, because /events/config is served by the primary and is thus
// unreachable exactly when the fallback is needed (primary down). Empty = disabled.
declare const __FALLBACK_INGEST_URL__: string;
export const FALLBACK_INGEST_URL: string =
  typeof __FALLBACK_INGEST_URL__ !== "undefined" ? __FALLBACK_INGEST_URL__ : "";

// --- URL params ---
export const URL_PARAM_SELECTOR = "tst_select";

// --- Event ID prefix ---
export const EVENT_ID_PREFIX = "tst_";

// --- Cookie names ---
export const COOKIE_EXTERNAL_ID = "_tst_eid";

// --- Click ID parameter → cookie mapping ---
export const CLICK_ID_PARAMS: Record<string, ClickIdConfig> = {
  fbclid: {
    cookie: "_tst_fbc",
    format: (v: string) => "fb.1." + Date.now() + "." + v,
  },
  gclid: { cookie: "_tst_gclid", format: null },
  gbraid: { cookie: "_tst_gbraid", format: null },
  wbraid: { cookie: "_tst_wbraid", format: null },
  msclkid: { cookie: "_tst_msclk", format: null },
  li_fat_id: { cookie: "_tst_li", format: null },
  ttclid: { cookie: "_tst_ttclk", format: null },
  // Snapchat click id: the ScCid URL param on an ad click (maps to sc_click_id
  // in the Snapchat CAPI). The complementary _scid first-party cookie is read
  // separately in getClickIds (like TikTok's _ttp).
  ScCid: { cookie: "_tst_scclid", format: null },
  // Pinterest click id: the epik URL param (maps to user_data.click_id in the
  // Pinterest CAPI). Pinterest's own tag persists the same value in _epik.
  epik: { cookie: "_tst_epik", format: null },
  // Reddit click id: the rdt_cid URL param on an ad click (maps to the
  // event-level click_id in the Reddit CAPI, sent as-is, never hashed).
  rdt_cid: { cookie: "_tst_rdtcid", format: null },
  // OpenAI Ads attribution reference. Their pixel documents that it "captures
  // oppref from the landing page URL" but never names the URL parameter; this
  // entry bets it is literally `oppref`. Wrong bet = no harm (the parameter
  // never appears and the __oppref cookie fallback in getClickIds still
  // catches it); right bet = arrival attribution works without their pixel.
  // Verify against a real ad click and record it in the payload-audit doc.
  oppref: { cookie: "_tst_oppref", format: null },
  // X (Twitter) click id: the twclid URL param on an ad click. It was in the
  // resolver's taxonomy from day one but never captured, so X ad traffic
  // could not resolve as paid.
  twclid: { cookie: "_tst_twclid", format: null },
};

// --- Data attribute names ---
export const DATA_ATTR_EVENT = "data-tst-event";
export const DATA_ATTR_VALUE = "data-tst-value";
export const DATA_ATTR_CURRENCY = "data-tst-currency";
export const DATA_ATTR_DETECT_EMAIL = "data-tst-detect-email";

export const DATA_ATTR_MAP: Record<string, DataAttributeConfig> = {
  "data-tst-content-id": { key: "content_ids", parse: (v) => v.split(",") },
  "data-tst-content-type": { key: "content_type", parse: (v) => v },
  "data-tst-content-name": { key: "content_name", parse: (v) => v },
  "data-tst-category": { key: "content_category", parse: (v) => v },
  "data-tst-num-items": { key: "num_items", parse: (v) => parseInt(v, 10) },
  "data-tst-order-id": { key: "order_id", parse: (v) => v },
  "data-tst-search": { key: "search_string", parse: (v) => v },
};

// --- Custom data key mapping (camelCase → snake_case for FB CAPI) ---
export const CUSTOM_DATA_MAP: Record<string, string> = {
  contentIds: "content_ids",
  contentType: "content_type",
  contentName: "content_name",
  numItems: "num_items",
  orderId: "order_id",
  contentCategory: "content_category",
  searchString: "search_string",
  query: "search_string",
};

/**
 * Facebook user data parameter abbreviations.
 * em = email, ph = phone, fn = firstName, ln = lastName,
 * ct = city, st = state, zp = zipCode
 */
export const FB_USER_DATA_KEYS = [
  "em",
  "ph",
  "fn",
  "ln",
  "ct",
  "st",
  "zp",
  "country",
  "external_id",
] as const;

export const FB_CUSTOM_DATA_KEYS = [
  "content_ids",
  "content_type",
  "content_name",
  "num_items",
  "order_id",
  "content_category",
] as const;

// --- User data field mapping (public API → FB parameter names) ---
export const USER_DATA_FIELD_MAP: Record<string, string> = {
  email: "em",
  phone: "ph",
  firstName: "fn",
  lastName: "ln",
  city: "ct",
  state: "st",
  zip: "zp",
  country: "country",
};

// --- Selector mode ---
export const BRAND_COLOR = "#d67a5c";
export const MAX_SELECTOR_DEPTH = 4;
export const MAX_ELEMENT_TEXT_LENGTH = 100;

// --- Click-rule text matching ---
// A non-interactive ancestor may only text-match a click rule when its own
// text is at most this long: containers (and ultimately <body>, whose
// innerText is the whole page) must never match a button-sized rule text.
export const MAX_CLICK_RULE_TEXT_LENGTH = 150;

// --- Custom parameter sanitation (GTM template / public track() passthrough) ---
export const CUSTOM_PARAM_KEY_PATTERN = /^[a-zA-Z0-9_]{1,40}$/;
export const MAX_CUSTOM_PARAMS = 20;
export const MAX_CUSTOM_STRING_LENGTH = 500;
export const MAX_CUSTOM_ARRAY_ITEMS = 25;
/** Free-form keys that smell like PII belong in setUserData (hashed), not custom data */
export const PII_KEY_BLOCKLIST = ["email", "e_mail", "phone", "telephone", "firstname", "first_name", "lastname", "last_name", "name", "address", "street", "zip", "zipcode", "postal_code", "ssn", "password"];

// --- Default currency ---
export const DEFAULT_CURRENCY = "EUR";

// --- Default API URL (development fallback) ---
export const DEV_API_URL = "http://localhost:3140";
